// CRM TanStack Query v5 client. Lives ONLY inside the CRM module — never shared with TG (v3).
import { QueryClient } from "@tanstack/react-query";

export const crmQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, err) => {
        if (err?.response?.status >= 400 && err?.response?.status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

// User-scoped localStorage keys cleared on logout. The next user signing in on
// the same browser must not inherit the previous user's selections (project,
// org, auth tokens). UI-only browser preferences (theme palette, sidebar
// collapse, dashboard view toggle) are intentionally NOT cleared — they're
// per-device, not per-user.
const USER_SCOPED_STORAGE_KEYS = [
  // CRM module
  "crm.selectedProjectId",
  // TG host — set during login by AuthContext (the bag of org metadata the
  // header/admin views read). Logout must clear it; AuthContext.login also
  // clears it before writing fresh, but on logout it was being left behind.
  "organization",
  // Admin-switch session flags. AuthContext clears these already, but listed
  // here so a brute-force /loop or 401-redirect path can't miss them.
  "isMainAdminSwitch",
  "mainAdminSwitchUserId",
];

// Per-user sessionStorage keys. Same rule as above.
const USER_SCOPED_SESSION_KEYS = [
  // CRM Meet-connect OAuth round-trip pivot.
  "crm-meet-connect-project",
  // useCurrency.js caches the current org's currency for the session.
  "currency",
];

// Logout hook for the whole app. Cancels in-flight requests, nukes both query
// clients (CRM v5 + TG v3), and wipes user-scoped client storage. Call this
// from AuthContext.logout's finally block so token clear + cache wipe stay in
// lockstep — otherwise the next user briefly sees the previous user's data
// while React Query re-validates. The function is best-effort: every step is
// wrapped in try/catch so a failure in one branch can't block the others.
export function clearCrmCache() {
  // CRM (TanStack Query v5)
  try { crmQueryClient.cancelQueries(); } catch (_e) {}
  try { crmQueryClient.cancelMutations(); } catch (_e) {}
  try { crmQueryClient.clear(); } catch (_e) {}
  try { crmQueryClient.removeQueries(); } catch (_e) {}

  // TG host has its own React Query client (in App.js). AuthContext clears
  // that one directly so we don't need a circular require here.

  for (const k of USER_SCOPED_STORAGE_KEYS) {
    try { localStorage.removeItem(k); } catch (_e) {}
  }
  for (const k of USER_SCOPED_SESSION_KEYS) {
    try { sessionStorage.removeItem(k); } catch (_e) {}
  }
}
